import type { ReactNode } from 'react'

interface FormSectionProps {
  title: string
  /** One line under the title, for what the fields themselves do not say. */
  description?: ReactNode
  /** Buttons that belong to the section — "Add variant", "Use option set". */
  action?: ReactNode
  /** Dropped between the header and the body, for a list-level error message. */
  alert?: ReactNode
  children: ReactNode
  className?: string
}

/**
 * One titled group of fields inside a long form.
 *
 * A hairline card rather than a filled one: `--surface` and `--overlay` are the
 * same white in the light theme and the same near-black in the dark one, so a
 * tinted card would read as a card in neither. The border does the grouping and
 * the dialog keeps its own background, which also stops the nesting from piling
 * up — rows inside a section are separated rather than boxed again.
 */
export function FormSection({
  title,
  description,
  action,
  alert,
  children,
  className,
}: FormSectionProps) {
  return (
    <section className={`border-border rounded-2xl border p-4 sm:p-5 ${className ?? ''}`.trimEnd()}>
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h3 className="text-[13px] leading-5 font-semibold tracking-tight">{title}</h3>
          {description && (
            <p className="text-muted mt-0.5 text-xs leading-relaxed">{description}</p>
          )}
        </div>

        {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
      </header>

      {alert}

      <div className="mt-4">{children}</div>
    </section>
  )
}

/**
 * The empty state inside a section — a dashed box saying what is missing and why
 * that is a normal thing for it to be.
 */
export function FormSectionEmpty({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted border-border rounded-xl border border-dashed px-3 py-6 text-center text-xs">
      {children}
    </p>
  )
}
